import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Search, X, Users, MapPin, Tag, Check, Filter, GraduationCap } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface AdvancedFiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: {
    students: string[];
    categories: string[];
    locations: string[];
    teachers: string[];
    showCompleted: boolean;
  };
  onFiltersChange?: (filters: any) => void;
  students: any[];
  categories: any[];
  locations: any[];
  teachers: any[];
}

const AdvancedFiltersModal: React.FC<AdvancedFiltersModalProps> = ({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  students,
  categories,
  locations,
  teachers,
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [localFilters, setLocalFilters] = useState({
    students: [] as string[],
    categories: [] as string[],
    locations: [] as string[],
    teachers: [] as string[],
    showCompleted: true,
  });

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, open]);

  const searchResults = React.useMemo(() => {
    if (!searchQuery) return { students: [], categories: [], locations: [], teachers: [] };

    const query = searchQuery.toLowerCase();

    return {
      students: students.filter(student => {
        const name = student.name || `${student.firstname || ''} ${student.lastname || ''}`;
        return name.toLowerCase().includes(query);
      }),
      categories: categories.filter(category =>
        category.name.toLowerCase().includes(query)
      ),
      locations: locations.filter(location =>
        location.name.toLowerCase().includes(query)
      ),
      teachers: teachers.filter(teacher => {
        const name = teacher.name || `${teacher.firstname || ''} ${teacher.lastname || ''}`;
        return name.toLowerCase().includes(query);
      }),
    };
  }, [searchQuery, students, categories, locations, teachers]);

  const toggleFilter = (type: 'students' | 'categories' | 'locations' | 'teachers', id: string) => {
    setLocalFilters(prev => {
      const current = prev[type];
      const isActive = current.includes(id);

      return {
        ...prev,
        [type]: isActive
          ? current.filter(item => item !== id)
          : [...current, id],
      };
    });
  };

  const selectAllOfType = (type: 'students' | 'categories' | 'locations' | 'teachers') => {
    const allIds = (type === 'students' ? students : type === 'categories' ? categories : type === 'locations' ? locations : teachers).map(item => item.id);
    setLocalFilters(prev => ({
      ...prev,
      [type]: allIds,
    }));
  };

  const clearAllOfType = (type: 'students' | 'categories' | 'locations' | 'teachers') => {
    setLocalFilters(prev => ({
      ...prev,
      [type]: [],
    }));
  };

  const applyFilters = () => {
    if (onFiltersChange) {
      onFiltersChange(localFilters);
    }
    onOpenChange(false);
  };

  const resetFilters = () => {
    setLocalFilters({
      students: [],
      categories: [],
      locations: [],
      teachers: [],
      showCompleted: true,
    });
  };

  const renderFilterSection = (
    title: string,
    icon: React.ReactNode,
    items: any[],
    type: 'students' | 'categories' | 'locations' | 'teachers'
  ) => {
    const activeItems = localFilters[type];
    const displayItems = searchQuery ? searchResults[type] : items;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="text-sm font-medium">{title}</h3>
            <Badge variant="outline" className="text-xs">
              {activeItems.length}/{items.length}
            </Badge>
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => selectAllOfType(type)}
              className="text-xs h-6 px-2"
            >
              Tout
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => clearAllOfType(type)}
              className="text-xs h-6 px-2"
            >
              Rien
            </Button>
          </div>
        </div>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {displayItems.map(item => {
            const isActive = activeItems.includes(item.id);
            let name;
            if (type === 'students' || type === 'teachers') {
              name = item.name || `${item.firstname} ${item.lastname}`;
            } else {
              name = item.name;
            }

            return (
              <div
                key={item.id}
                onClick={() => toggleFilter(type, item.id)}
                className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${
                  isActive
                    ? 'bg-primary/10 border-primary/20 border'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <div className="flex items-center gap-2 flex-1">
                  {type === 'categories' && (
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color || '#888888' }}
                    />
                  )}
                  <span className="text-sm truncate">{name}</span>
                </div>
                {isActive && <Check className="h-4 w-4 text-primary" />}
              </div>
            );
          })}
        </div>
        {displayItems.length === 0 && searchQuery && (
          <div className="text-center py-4 text-gray-500 text-sm">
            Aucun résultat trouvé
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtres avancés
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher étudiants, catégories, emplacements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <Switch
              id="show-completed"
              checked={localFilters.showCompleted}
              onCheckedChange={(checked) =>
                setLocalFilters(prev => ({ ...prev, showCompleted: checked }))
              }
            />
            <Label htmlFor="show-completed" className="text-sm">
              Afficher les cours terminés
            </Label>
          </div>

          <ScrollArea className="h-[400px]">
            <div className="space-y-6 pr-4">
              {students.length > 0 && (
                <>
                  {renderFilterSection(
                    t('agenda.students'),
                    <Users className="h-4 w-4 text-gray-500" />,
                    students,
                    'students'
                  )}
                  <Separator />
                </>
              )}
              {teachers.length > 0 && (
                <>
                  {renderFilterSection(
                    t('agenda.teachers'),
                    <GraduationCap className="h-4 w-4 text-gray-500" />,
                    teachers,
                    'teachers'
                  )}
                  <Separator />
                </>
              )}
              {categories.length > 0 && (
                <>
                  {renderFilterSection(
                    t('agenda.categories'),
                    <Tag className="h-4 w-4 text-gray-500" />,
                    categories,
                    'categories'
                  )}
                  <Separator />
                </>
              )}
              {locations.length > 0 && (
                renderFilterSection(
                  t('agenda.locations'),
                  <MapPin className="h-4 w-4 text-gray-500" />,
                  locations,
                  'locations'
                )
              )}
            </div>
          </ScrollArea>

          <div className="flex gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={resetFilters}
              className="flex-1"
            >
              Réinitialiser
            </Button>
            <Button
              onClick={applyFilters}
              className="flex-1"
            >
              Appliquer les filtres
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedFiltersModal;
